import {
  apiGetCart,
  apiAddToCart,
  apiDecrementCart,
  apiRemoveCartItem,
  apiClearCart,
} from "../../api/cart.api";
import { resolveProduct } from "../productIndex";
import { cartItemsToMap } from "./session.helpers";

function addGuestItemToCart({ setCart, productId, product, qty = 1 }) {
  setCart((prev) => {
    const current = prev?.[productId] || {
      ...product,
      id: productId,
      product_id: productId,
      qty: 0,
    };

    return {
      ...(prev || {}),
      [productId]: {
        ...current,
        qty: Number(current.qty || 0) + Number(qty || 1),
      },
    };
  });
}

function decrementGuestItem({ setCart, productId, qty = 1 }) {
  setCart((prev) => {
    const current = prev?.[productId];
    if (!current) return prev || {};

    const nextQty = Number(current.qty || 1) - Number(qty || 1);

    if (nextQty <= 0) {
      const next = { ...(prev || {}) };
      delete next[productId];
      return next;
    }

    return {
      ...(prev || {}),
      [productId]: {
        ...current,
        qty: nextQty,
      },
    };
  });
}

function removeGuestItem({ setCart, productId }) {
  setCart((prev) => {
    const next = { ...(prev || {}) };
    delete next[productId];
    return next;
  });
}

function clearGuestCart({ setCart }) {
  setCart({});
  localStorage.removeItem("guest_cart");
}

export async function syncCartFromServer({ setCart }) {
  const res = await apiGetCart();

  if (res?.unauthenticated) {
    return { unauthenticated: true };
  }

  setCart(cartItemsToMap(res?.items || []));
  return res;
}

export async function syncGuestCartToServer() {
  try {
    const saved = localStorage.getItem("guest_cart");
    if (!saved) return;

    const guestCart = JSON.parse(saved);
    const items = Object.values(guestCart || {});

    for (const item of items) {
      const productId = item?.id || item?.product_id || item?.variant_id;
      const qty = Number(item?.qty || item?.quantity || 1);

      if (!productId) continue;

      await apiAddToCart(
        { ...item, id: productId, product_id: productId },
        qty
      );
    }

    localStorage.removeItem("guest_cart");
  } catch (e) {
    console.error("syncGuestCartToServer error:", e);
  }
}

export async function handleAddToCart({
  action,
  productIndexRef,
  setCart,
  setMessages,
}) {
  const prod = resolveProduct(productIndexRef, action);
  const product = prod || action?.product || {};

  const productId = String(
    action?.product_id ||
      product?.id ||
      product?.product_id ||
      product?.variant_id ||
      ""
  );

  const qty = Number(action?.qty || 1);

  if (!productId) {
    setMessages((m) => [
      ...m,
      { role: "assistant", text: "⚠️ Не зміг додати товар у кошик" },
    ]);
    return { ok: false, reason: "invalid_product" };
  }

  try {
    const res = await apiAddToCart(
      { ...product, id: productId, product_id: productId },
      qty
    );

    if (res?.unauthenticated) {
      addGuestItemToCart({ setCart, productId, product, qty });
      return { ok: true, guest: true };
    }

    setCart(cartItemsToMap(res?.items || []));
    return { ok: true };
  } catch (e) {
    console.error("add_to_cart error:", e);

    const status = e?.status || e?.response?.status;

    if (status === 401 || status === 403) {
      addGuestItemToCart({ setCart, productId, product, qty });
      return { ok: true, guest: true };
    }

    setMessages((m) => [
      ...m,
      { role: "assistant", text: "⚠️ Не зміг додати товар у кошик" },
    ]);

    return { ok: false, reason: "request_failed" };
  }
}

export async function handleDecrementCart({
  action,
  cart,
  setCart,
  setMessages,
}) {
  const productId = String(action?.product_id || "");
  const qty = Number(action?.qty || 1);

  if (!productId) return { ok: false, reason: "invalid_product" };

  try {
    const res = await apiDecrementCart(productId, qty);

    if (res?.unauthenticated) {
      decrementGuestItem({ setCart, productId, qty });
      return { ok: true, guest: true };
    }

    setCart(cartItemsToMap(res?.items || []));
    return { ok: true };
  } catch (e) {
    console.error("decrement_cart error:", e);

    const status = e?.status || e?.response?.status;

    if (status === 401 || status === 403) {
      decrementGuestItem({ setCart, productId, qty });
      return { ok: true, guest: true };
    }

    return { ok: false, reason: "request_failed" };
  }
}

export async function handleRemoveFromCart({
  action,
  cart,
  setCart,
  setMessages,
}) {
  const productId = String(action?.product_id || "");
  if (!productId) return { ok: false, reason: "invalid_product" };

  try {
    const res = await apiRemoveCartItem(productId);

    if (res?.unauthenticated) {
      removeGuestItem({ setCart, productId });
      return { ok: true, guest: true };
    }

    setCart(cartItemsToMap(res?.items || []));
    return { ok: true };
  } catch (e) {
    console.error("remove_from_cart error:", e);

    const status = e?.status || e?.response?.status;

    if (status === 401 || status === 403) {
      removeGuestItem({ setCart, productId });
      return { ok: true, guest: true };
    }

    return { ok: false, reason: "request_failed" };
  }
}

export async function handleClearCart({ setCart, setMessages }) {
  try {
    const res = await apiClearCart();

    if (res?.unauthenticated) {
      clearGuestCart({ setCart });
      return { ok: true, guest: true };
    }

    clearGuestCart({ setCart });
    return { ok: true };
  } catch (e) {
    console.error("clear_cart error:", e);

    const status = e?.status || e?.response?.status;

    if (status === 401 || status === 403) {
      clearGuestCart({ setCart });
      return { ok: true, guest: true };
    }

    return { ok: false, reason: "request_failed" };
  }
}